/**
 * 網址清理 —— 前後端共用。
 *
 * 通用規則刻意保守：query 與 hash 全砍。
 * Meta 系（Threads / Facebook / Instagram / Messenger）是優先支援的目標，
 * 而它剛好有兩類非通用行為必須處理：
 *
 *   1. link shim —— 真正的網址被塞在 query 裡（l.facebook.com/l.php?u=...），
 *      這種要把 u 拆出來當結果，不是把它砍掉。
 *   2. 身分參數 —— facebook.com/profile.php?id=... 這類 query 就是身分，
 *      砍掉之後網址直接失效，所以要留。
 *
 * 這兩件事都必須在「不連網」的情況下也能做，所以放在 shared 而不是 worker。
 */

/** 手機版 / 地區版 / 裸網域 → 標準網域。路徑是 1:1 對應的才放進來。 */
const HOST_ALIASES: Record<string, string> = {
  'facebook.com': 'www.facebook.com',
  'm.facebook.com': 'www.facebook.com',
  'web.facebook.com': 'www.facebook.com',
  'mbasic.facebook.com': 'www.facebook.com',
  'touch.facebook.com': 'www.facebook.com',
  'free.facebook.com': 'www.facebook.com',
  'd.facebook.com': 'www.facebook.com',
  'instagram.com': 'www.instagram.com',
  // Meta 已把 threads.net 整域搬到 threads.com，路徑不變。
  'threads.net': 'www.threads.com',
  'www.threads.net': 'www.threads.com',
  'threads.com': 'www.threads.com',
  'messenger.com': 'www.messenger.com',
}

/**
 * Link shim —— 真網址藏在 query 裡。
 * key 是 host，value 是裝著網址的參數名。
 */
const SHIM_HOSTS: Record<string, string> = {
  'l.facebook.com': 'u',
  'lm.facebook.com': 'u',
  'l.instagram.com': 'u',
  'l.messenger.com': 'u',
  'l.threads.com': 'u',
  'l.threads.net': 'u',
  'away.vk.com': 'to',
}

/** host + 路徑前綴 → 必須保留的 query 參數。 */
const KEEP_PARAMS: { host: string; path: RegExp; keep: string[] }[] = [
  // Facebook 的舊式端點，query 就是主鍵。
  { host: 'www.facebook.com', path: /^\/profile\.php$/, keep: ['id'] },
  {
    host: 'www.facebook.com',
    path: /^\/story\.php$/,
    keep: ['story_fbid', 'id'],
  },
  {
    host: 'www.facebook.com',
    path: /^\/permalink\.php$/,
    keep: ['story_fbid', 'id', 'comment_id'],
  },
  { host: 'www.facebook.com', path: /^\/photo\.php$/, keep: ['fbid', 'set'] },
  { host: 'www.facebook.com', path: /^\/photo\/?$/, keep: ['fbid', 'set'] },
  { host: 'www.facebook.com', path: /^\/video\.php$/, keep: ['v'] },
  { host: 'www.facebook.com', path: /^\/watch\.php$/, keep: ['v'] },
  { host: 'www.facebook.com', path: /^\/watch\/?$/, keep: ['v'] },
  { host: 'www.facebook.com', path: /^\/media\/set\/?$/, keep: ['set'] },
  { host: 'www.facebook.com', path: /^\/groups\/[^/]+\/?$/, keep: [] },
  // 通用：非 Meta 站台目前一律全砍（依需求，其他站不特別調校）。
]

/**
 * 把網址整理成「可以拿去跟隨轉址」的形式：正規化網域、展開 link shim。
 *
 * 這一步必須在**發請求之前**做。shim 的真網址就藏在 query 裡，先展開才能
 * 追對目標；否則會白打一次 l.facebook.com（它會回 400），而且展開後的目標
 * 如果本身也是短鏈就永遠追不到。
 */
export function normalizeTarget(raw: string | URL): { url: URL; notes: string[] } | null {
  const parsed = typeof raw === 'string' ? coerceUrl(raw) : coerceUrl(raw.toString())
  if (!parsed) return null
  const notes: string[] = []
  applyHostAlias(parsed, notes)
  return { url: unwrapShims(parsed, notes), notes }
}

export interface CleanResult {
  /** 純網址：query / hash 已拔除（身分參數保留）。 */
  clean: string
  /** 展開 shim、正規化 host 之後，但 query 還在的版本。 */
  full: string
  /** clean 與 full 是否不同 —— 決定 UI 要不要顯示第二行。 */
  trimmed: boolean
  /** 對使用者說明做了什麼。 */
  notes: string[]
}

function tryParse(s: string): URL | null {
  try {
    return new URL(s)
  } catch {
    return null
  }
}

/**
 * 補上 scheme；使用者常常只貼 `threads.com/...`。
 *
 * 先原樣 parse 再決定 —— 不能只看有沒有 `://`，因為 `mailto:a@b.com`
 * 沒有斜線，硬加 https:// 會變成 `https://mailto:a@b.com`（host 竟然是 b.com，
 * 完全通過驗證）。先 parse 就能靠 protocol 把這類擋掉。
 *
 * 代價：沒有 scheme 的 `example.com:8080/x` 會被當成 protocol 而拒收。
 * 短網址不會長這樣，而 guard 本來也只放行 80/443，所以不特別處理。
 */
export function coerceUrl(raw: string): URL | null {
  const s = raw.trim()
  if (!s) return null

  const direct = tryParse(s)
  if (direct) {
    if (direct.protocol !== 'http:' && direct.protocol !== 'https:') return null
    return direct.hostname.includes('.') ? direct : null
  }

  const guessed = tryParse(`https://${s}`)
  if (!guessed) return null
  if (guessed.protocol !== 'http:' && guessed.protocol !== 'https:') return null
  return guessed.hostname.includes('.') ? guessed : null
}

function applyHostAlias(u: URL, notes: string[]): void {
  const alias = HOST_ALIASES[u.hostname.toLowerCase()]
  if (alias && alias !== u.hostname) {
    notes.push(`網域正規化 ${u.hostname} → ${alias}`)
    u.hostname = alias
  }
}

/**
 * 展開 link shim。最多三層 —— shim 包 shim 真的會發生
 * （Messenger 分享一個已經被 l.facebook.com 包過的連結）。
 */
function unwrapShims(u: URL, notes: string[]): URL {
  let current = u
  for (let depth = 0; depth < 3; depth++) {
    const param = SHIM_HOSTS[current.hostname.toLowerCase()]
    if (!param) break
    const inner = current.searchParams.get(param)
    if (!inner) break
    const parsed = coerceUrl(inner)
    if (!parsed) break
    notes.push(`展開 ${current.hostname} 轉址包裝`)
    current = parsed
    applyHostAlias(current, notes)
  }
  return current
}

function paramsToKeep(u: URL): string[] {
  const host = u.hostname.toLowerCase()
  for (const rule of KEEP_PARAMS) {
    if (rule.host === host && rule.path.test(u.pathname)) return rule.keep
  }
  return []
}

/**
 * 主入口。永遠會回傳結果 —— 連不成 URL 的字串在上層就被擋掉了。
 */
export function cleanUrl(raw: string): CleanResult | null {
  const normalized = normalizeTarget(raw)
  if (!normalized) return null

  const { url: target, notes } = normalized
  const full = target.toString()

  const cleaned = new URL(full)
  cleaned.hash = ''

  const keep = paramsToKeep(cleaned)
  const dropped: string[] = []
  for (const key of [...cleaned.searchParams.keys()]) {
    if (!keep.includes(key)) {
      cleaned.searchParams.delete(key)
      if (!dropped.includes(key)) dropped.push(key)
    }
  }
  // searchParams 全空時 toString() 會留下一個裸 "?"，手動清掉。
  if ([...cleaned.searchParams.keys()].length === 0) cleaned.search = ''

  if (dropped.length) {
    notes.push(`移除參數 ${dropped.map((k) => `?${k}`).join(' ')}`)
  }
  if (keep.length && [...cleaned.searchParams.keys()].length) {
    notes.push(`保留 ${[...cleaned.searchParams.keys()].join(' / ')} —— 這是網址的身分`)
  }
  if (target.hash) notes.push(`移除片段 ${target.hash}`)

  const clean = cleaned.toString()
  return { clean, full, trimmed: clean !== full, notes }
}

/** 抓字串裡的第一個網址。 */
const URL_RE = /https?:\/\/[^\s<>"'`]+/i
/** 尾巴常常黏到標點 —— 全形半形都要處理。 */
const TRAILING = /[.,;:!?)\]}>'"「」『』】），。、！？…]+$/

export function extractUrl(text: string): string | null {
  const m = URL_RE.exec(text)
  if (!m) return null
  let hit = m[0].replace(TRAILING, '')
  // 括號成對時不該砍掉右括號（維基百科式網址）。
  const opens = (hit.match(/\(/g) ?? []).length
  const closes = (hit.match(/\)/g) ?? []).length
  if (opens > closes && m[0].includes(')')) hit = `${hit})`
  return hit || null
}

/**
 * Share target 的參數可能把網址放在 url、text 或 title 任一個欄位，
 * Android 各家 app 塞法不一致，所以三個都要撈。
 */
export function urlFromShare(params: URLSearchParams): string | null {
  for (const key of ['url', 'text', 'title'] as const) {
    const raw = params.get(key)?.trim()
    if (!raw) continue
    // 整個欄位就是網址時直接用，否則從夾雜文字裡撈出來。
    if (/^https?:\/\/\S+$/i.test(raw)) return raw
    const found = extractUrl(raw)
    if (found) return found
  }
  return null
}
